import { type ReactElement } from "react";
import { TriangleAlert, CircleCheck, Search } from "lucide-react";
import { Button, Checkbox, Input } from "../atoms/index.js";
import { cn } from "../lib/utils.js";
import type { ProposalReviewItemsModel } from "./proposal-review.types.js";

/**
 * The affected-targets column.
 *
 * A single-select LIST, built as radios rather than `role="option"`: the rows
 * carry a nested interactive control (Remove), and an option containing a
 * button is the nested-interactive-role defect this repo has already shipped
 * once — see `docs/learnings/2026-07-12-axe-nested-interactive-role-option.md`.
 * A radiogroup states "one of these is selected" and tolerates the extra
 * control beside it.
 */
export function ProposalReviewItemList({
  model,
  readOnly,
}: {
  model: ProposalReviewItemsModel;
  readOnly: boolean;
}): ReactElement {
  return (
    <div className="flex min-h-0 flex-col">
      <h3 className="mb-2 text-label font-semibold text-pb-text">
        {model.label}
      </h3>
      <Input
        value={model.searchValue}
        onChange={(event) => model.onSearchChange(event.target.value)}
        placeholder={model.searchPlaceholder ?? "Search"}
        aria-label={`Search ${model.label.toLowerCase()}`}
        prefix={<Search className="h-3.5 w-3.5" aria-hidden />}
      />
      {model.rows.length === 0 ? (
        <p className="mt-3 text-dense-1 text-pb-text-muted">
          {model.emptyLabel ?? "Nothing to show."}
        </p>
      ) : (
        <ul
          role="radiogroup"
          aria-label={model.label}
          className="mt-2 min-h-0 flex-1 space-y-1 overflow-y-auto pe-1"
        >
          {model.rows.map((row) => {
            const selected = row.id === model.selectedId;
            return (
              <li key={row.id} role="none">
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-2 py-2 transition-colors",
                    selected
                      ? "border-pb-accent/40 bg-pb-accent/10"
                      : "border-transparent hover:pb-overlay-paint",
                  )}
                >
                  {model.onToggle && !readOnly ? (
                    <Checkbox
                      checked={row.included === true}
                      aria-label={`Include ${row.reference ?? row.title} in approval`}
                      onChange={(included) =>
                        model.onToggle?.(row.id, included)
                      }
                    />
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => model.onSelect(row.id)}
                    className="h-auto min-w-0 flex-1 justify-start p-0 text-start"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "h-3.5 w-3.5 shrink-0 rounded-full border",
                        selected
                          ? "border-pb-teal bg-pb-teal"
                          : "border-pb-control-border",
                      )}
                    />
                    <span className="min-w-0">
                      {row.reference ? (
                        <span className="block truncate text-label font-medium text-pb-text">
                          {row.reference}
                        </span>
                      ) : null}
                      <span
                        className={cn(
                          "block truncate",
                          row.reference
                            ? "text-dense-1 text-pb-text-muted"
                            : "text-label font-medium text-pb-text",
                        )}
                      >
                        {row.title}
                      </span>
                    </span>
                  </Button>
                  {/* State is never carried by colour alone: each status has its
                      own icon and its own accessible name. */}
                  {row.status === "valid" ? (
                    <CircleCheck
                      className="h-4 w-4 shrink-0 text-pb-green-text"
                      aria-label={row.statusHint ?? "Valid"}
                    />
                  ) : (
                    <TriangleAlert
                      className="h-4 w-4 shrink-0 text-pb-amber-text"
                      aria-label={row.statusHint ?? "Needs attention"}
                    />
                  )}
                  {model.onRemove !== undefined && !readOnly ? (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => model.onRemove?.(row.id)}
                      aria-label={`Remove ${row.reference ?? row.title}`}
                      className="h-auto shrink-0 p-0 text-dense-1"
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-3 flex items-center gap-4 border-t border-pb-border pt-2 text-dense-2 text-pb-text-muted">
        <span className="inline-flex items-center gap-1">
          <CircleCheck className="h-3.5 w-3.5 text-pb-green-text" aria-hidden />
          Valid
        </span>
        <span className="inline-flex items-center gap-1">
          <TriangleAlert
            className="h-3.5 w-3.5 text-pb-amber-text"
            aria-hidden
          />
          Needs attention
        </span>
      </div>
    </div>
  );
}
